import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';

import { MediaItem } from '../../types';
import PlyrPlayer from '../PlyrPlayer';

import EmptyView from './EmptyView';

interface MediaEvent {
  duration: number;
  currentTime: number;
}

interface MediaPlayerProps {
  media?: MediaItem;
  loading?: boolean;
  error?: string;
  showNameWhenProcessing?: boolean;
  allowDownload?: boolean;
  trackSession?: boolean;

  onMediaPlay?: (event: MediaEvent) => void;
  onMediaTimeUpdate?: (event: MediaEvent) => void;
  onMediaSeeked?: (event: MediaEvent) => void;
  onMediaEnded?: (event: MediaEvent) => void;
}

const MediaPlayer = (props: MediaPlayerProps) => {
  const {
    media,
    error,
    showNameWhenProcessing = false,
    allowDownload = false,
    loading = false,
    trackSession = false,
    onMediaPlay,
    onMediaTimeUpdate,
    onMediaSeeked,
    onMediaEnded,
  } = props;
  const sources = media?.sources;

  const intl = useIntl();
  const i18n = {
    quality: intl.formatMessage({ id: 'mediaPlayer.quality' }),
    speed: intl.formatMessage({ id: 'mediaPlayer.speed' }),
  };

  const controls = ['play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'settings', 'fullscreen'];

  let urls = {};
  if (allowDownload && sources && sources.length > 1) {
    controls.push('download');
    const hdSource = sources[sources.length - 1];
    urls = {
      download: hdSource.src,
    };
  }
  const source = useMemo(() => {
    if (sources && sources.length) {
      const mediaType = media.type !== 'image' ? media.type : 'video';
      return {
        type: mediaType,
        sources,
        title: media.name,
      };
    }
    return null;
  }, [media, sources]);

  return (
    <Box width="100%">
      <EmptyView media={media} error={error} loading={loading} showName={showNameWhenProcessing} />
      {source && media?.processed && (
        <PlyrPlayer
          source={source}
          options={{
            urls,
            i18n,
            fullscreen: {
              iosNative: true,
            },
            controls,
          }}
          trackSession={trackSession}
          onPlay={onMediaPlay}
          onTimeUpdate={onMediaTimeUpdate}
          onSeeked={onMediaSeeked}
          onEnded={onMediaEnded}
        />
      )}
    </Box>
  );
};

export default MediaPlayer;
